import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { ReportTypesEnum } from '../enums/reportTypes';
import { GetAttendanceLogs } from '../services/AttendanceLogs/GetAttendanceLogs';
import { PostReport } from '../services/Reports/PostReport';
import { GetReportsList } from '../services/Reports/GetReportsList';
import { GetProjects } from '../services/ProjectTagging/GetProjects';
import { PutProjectLogs } from '../services/ProjectTagging/PutProjectLogs';
import { DeleteProjectLog } from '../services/ProjectTagging/DeleteProjectLog';
import { PostProjectLogsMyAttendance } from '../services/ProjectTagging/PostProjectLogsMyAttendance';
import { useReportStore } from './report';

export const useMyAttendanceStore = defineStore('myAttendance', () => {
  const reportStore = useReportStore();
  const attendanceLogs = ref([]);
  const attendanceLogsPagination = ref({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 });
  const isAttendanceLogsLoading = ref(false);
  const isAttendanceLogsInitialState = ref(true);
  const dateFrom = ref('');
  const dateTo = ref('');
  const isAttendanceLogsActive = ref(true);
  watch(isAttendanceLogsActive, (newVal) => {
    const isExportLogsActive = !newVal;
    if (isExportLogsActive) {
      getExportedAttendanceLogs({
        PageNumber: 1,
        RowsPerPage: 10,
      });
    }
  });
  const searchAttendanceLogs = async ({ DateFrom, DateTo, PageNumber, RowsPerPage }) => {
    isAttendanceLogsLoading.value = true;
    try {
      const response = await GetAttendanceLogs({
        DateFrom,
        DateTo,
        PageNumber,
        RowsPerPage,
      });
      if (response.errors) {
        attendanceLogs.value = [];
        return;
      }
      dateFrom.value = DateFrom;
      dateTo.value = DateTo;
      attendanceLogs.value = response.data;
      attendanceLogsPagination.value = response.pagination;
    } finally {
      isAttendanceLogsInitialState.value = false;
      isAttendanceLogsLoading.value = false;
    }
  };
  const resetAttendanceLogs = () => {
    isAttendanceLogsInitialState.value = true;
    attendanceLogs.value = [];
    attendanceLogsPagination.value = { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 };
    dateFrom.value = '';
    dateTo.value = '';
  };
  const isWholePageLoading = ref(false);
  const exportAttendanceLogs = async ({ DateFrom, DateTo }) => {
    isWholePageLoading.value = true;
    try {
      await PostReport({
        DateFrom,
        DateTo,
        ReportType: ReportTypesEnum.myAttendance,
      });
      reportStore.toggleInfoToast();
      isAttendanceLogsActive.value = false;
    } finally {
      isWholePageLoading.value = false;
    }
  };
  const exportedLogs = ref([]);
  const exportedLogsPagination = ref({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 });
  const isExportedLogsLoading = ref(false);
  const isExportedLogsInitialState = ref(true);
  const getExportedAttendanceLogs = async ({ PageNumber, RowsPerPage }) => {
    isExportedLogsLoading.value = true;
    try {
      const response = await GetReportsList({
        PageNumber,
        RowsPerPage,
        ReportType: ReportTypesEnum.myAttendance,
      });
      if (response.errors) {
        exportedLogs.value = [];
        return;
      }
      exportedLogs.value = response.data;
      exportedLogsPagination.value = response.pagination;
    } finally {
      isExportedLogsInitialState.value = false;
      isExportedLogsLoading.value = false;
    }
  };
  const projectTaggingModalData = ref({
    bioEmpID: '',
    inOutMode: '',
    logDate: '',
    logDetail: '',
    logID: '',
    logTime: '',
    notes: '',
    projectLog: {},
  });
  const editProjectModal = ref(false);
  const removeProjectModal = ref(false);
  const createProjectModal = ref(false);
  const confirmProjectModal = ref(false);
  const projectList = ref([]);
  const selectedProjectLabel = ref('–');
  const selectedProjectPopover = ref(null);
  const isListOpen = ref(false);
  const projectListLoader = ref(false);
  const projectListCurrentPage = ref(1);
  const projectListTotalPages = ref(0);
  const isFirstLoadEmpty = ref(false);
  const getProjectsViaEmpId = async (payload) => {
    const response = await GetProjects(payload);
    if (response.errors) {
      projectList.value = [];
      return;
    }
    projectList.value = response.data;
    projectListTotalPages.value = response.pagination.totalPages;
    projectListCurrentPage.value = response.pagination.currentPage;
  };
  const searchParams = ref({});
  const successProjectToast = ref(false);
  const putProjectLogs = async (payload) => {
    const response = await PutProjectLogs(payload);
    if (response.errors) {
      return;
    }
    editProjectModal.value = false;
    await searchAttendanceLogs({ ...searchParams.value });
    successProjectToast.value = true;
  };
  const successDeleteProjectToast = ref(false);
  const deleteProjectLog = async (payload) => {
    const response = await DeleteProjectLog(payload);
    if (response.errors) {
      return;
    }
    removeProjectModal.value = false;
    await searchAttendanceLogs({ ...searchParams.value });
    successDeleteProjectToast.value = true;
  };
  const successAddProjectToast = ref(false);
  const postProjectLogMyAttendance = async (payload) => {
    const response = await PostProjectLogsMyAttendance(payload);
    if (response.errors) {
      return;
    }
    createProjectModal.value = false;
    await searchAttendanceLogs({ ...searchParams.value });
    successAddProjectToast.value = true;
  };
  return {
    attendanceLogs,
    attendanceLogsPagination,
    exportedLogs,
    exportedLogsPagination,
    dateFrom,
    dateTo,
    isAttendanceLogsLoading,
    isAttendanceLogsInitialState,
    isAttendanceLogsActive,
    isExportedLogsLoading,
    isExportedLogsInitialState,
    isWholePageLoading,
    resetAttendanceLogs,
    searchAttendanceLogs,
    getExportedAttendanceLogs,
    exportAttendanceLogs,
    projectTaggingModalData,
    editProjectModal,
    confirmProjectModal,
    projectList,
    getProjectsViaEmpId,
    selectedProjectLabel,
    isListOpen,
    selectedProjectPopover,
    projectListLoader,
    projectListCurrentPage,
    projectListTotalPages,
    isFirstLoadEmpty,
    successProjectToast,
    putProjectLogs,
    searchParams,
    removeProjectModal,
    deleteProjectLog,
    successDeleteProjectToast,
    createProjectModal,
    postProjectLogMyAttendance,
    successAddProjectToast,
  };
});
